import React, { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Formik, Form, useFormikContext } from "formik";
import { ITempProfileData } from "../../../../../redux/profile/tempProfiles/slice";
import { NameInput, PhoneInput, EmailInput, TextareaInput } from "../../../../../components";
import { TextInput } from "../TextInput";
import profileIcon from "../../../../../assets/icons/profile-focus.svg";
import styles from "./GiftSection.module.scss";

const FormikReduxSync: React.FC<{
  profileId: string;
  profileData: ITempProfileData;
  updateProfile: (profileId: string, field: keyof ITempProfileData, value: string) => void;
  additionalFields: string[];
}> = ({ profileId, profileData, updateProfile, additionalFields }) => {
  const { values } = useFormikContext<any>();

  useEffect(() => {
    if (values.name !== profileData.name) {
      updateProfile(profileId, "name", values.name);
    }
    if (values.phone !== profileData.phone) {
      updateProfile(profileId, "phone", values.phone);
    }
    
    additionalFields.forEach((fieldKey) => {
      if (values[fieldKey] !== profileData[fieldKey as keyof ITempProfileData]) {
        updateProfile(profileId, fieldKey as keyof ITempProfileData, values[fieldKey]);
      }
    });
  }, [values, profileId, profileData, updateProfile, additionalFields]);

  return null;
};

interface AdditionalField {
  type: "email" | "default";
  label: string;
  placeholder: string;
  key: keyof ITempProfileData;
  multiline?: boolean;
}

interface GiftSectionProps {
  title: string;
  subtitle?: string;
  buttonText: string;
  profileId: "sender" | "receiver";
  profileType: "adult";
  profileNumber: number;
  tempProfilesData: { [profileId: string]: ITempProfileData };
  updateProfile: (profileId: string, field: keyof ITempProfileData, value: string) => void;
  showValidation?: boolean;
  validateProfile?: (profileData: ITempProfileData, isReceiver: boolean) => boolean;
  additionalFields?: AdditionalField[];
}

export const GiftSection: React.FC<GiftSectionProps> = ({
  title,
  subtitle,
  buttonText,
  profileId,
  profileType,
  profileNumber,
  tempProfilesData,
  updateProfile,
  showValidation = false,
  validateProfile,
  additionalFields = [],
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const profileData: ITempProfileData = tempProfilesData[profileId] || {
    name: "",
    phone: "",
    email: "",
    birthDate: "",
    message: "",
  };

  const isReceiver = profileId === "receiver";
  const isValid = validateProfile ? validateProfile(profileData, isReceiver) : false;

  const handleSelectProfile = () => {
    const currentPath = location.pathname;
    navigate("/subscriptions-profiles", {
      state: {
        profileId: profileId,
        profileType: profileType,
        profileNumber: profileNumber,
        returnPath: currentPath,
      },
    });
  };

  const initialValues: any = {
    name: profileData.name || "",
    phone: profileData.phone || "",
  };
  
  additionalFields.forEach((field) => {
    const fieldValue = profileData[field.key];
    initialValues[field.key] = typeof fieldValue === "string" ? fieldValue : String(fieldValue || "");
  });

  const additionalFieldKeys = additionalFields.map((field) => field.key as string);

  return (
    <div className={styles.giftSection}>
      <div className={styles.header}>
        <h3 className={styles.title}>
          {title}
        </h3>
        {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
      </div>

      <div className={styles.profileNumberContainer}>
        <p className={styles.profileNumber}>
          {profileType === "adult" ? `Взрослый №${profileNumber}` : `Детский №${profileNumber}`}
        </p>
        <button
          className={styles.selectButton}
          onClick={handleSelectProfile}
        >
          <img src={profileIcon} alt="Профиль" className={styles.selectButtonIcon} />
          <span className={styles.selectButtonText}>
            Выбрать
          </span>
        </button>
      </div>

      <Formik
        initialValues={initialValues}
        enableReinitialize={true}
        onSubmit={() => {}}
      >
        {() => (
          <Form>
            <FormikReduxSync
              profileId={profileId}
              profileData={profileData}
              updateProfile={updateProfile}
              additionalFields={additionalFieldKeys}
            />
            <div className={styles.fields}>
              <NameInput
                lable="ФИО"
                name="name"
                focusThisInput={false}
                placeholder="Введите ФИО"
              />

              <PhoneInput
                lable="Телефон"
                name="phone"
                focusThisInput={false}
                placeholder="+7 (999) 456-78-09"
              />

              {additionalFields.map((field, index) => {
                if (field.type === "email") {
                  return (
                    <EmailInput
                      key={`${field.key}-${index}`}
                      lable={field.label}
                      name={field.key as string}
                      focusThisInput={false}
                      placeholder={field.placeholder}
                    />
                  );
                }
                
                if (field.multiline) {
                  return (
                    <div key={`${field.key}-${index}`} className={styles.textareaField}>
                      <TextareaInput
                        name={field.key as string}
                        placeholder={field.placeholder}
                        className={styles.textarea}
                      />
                    </div>
                  );
                }
                
                const fieldValue = profileData[field.key];
                return (
                  <TextInput
                    key={`${field.key}-${index}`}
                    label={field.label}
                    value={typeof fieldValue === "string" ? fieldValue : String(fieldValue || "")}
                    onChange={(value) => updateProfile(profileId, field.key, value)}
                    placeholder={field.placeholder}
                    type="text"
                  />
                );
              })}
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

